import React from 'react';
import { Alert, Col, Form, FormInstance, InputNumber, Row, Select, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { supplierOrderQuantityRule } from '../../../dto/supplierOrder';
import {
  ALLOCATION_UNITS,
  convertAllocationQuantity,
  resolveThirdDimensionMm,
} from '../../../utils/dimensionConversion';
import { formatUnit } from '../../../utils/units';
import { SupplierOrderAllocationItemOption } from '../supplierOrderAllocationOptions';
import SupplierOrderManualCostOverride from './SupplierOrderManualCostOverride';

type FieldName = 'allocateByItem' | 'supplierOrderItemId' | 'supplierOrderItemIndex' | 'allocatedQuantity' | 'allocatedUnit' | 'allocatedCostPLN';

interface SupplierOrderLinkAllocationFieldsProps {
  form: FormInstance;
  canEditSupplierOrderFinance: boolean;
  supplierOrderItemOptions: SupplierOrderAllocationItemOption[];
  namePrefix?: (string | number)[];
  itemFieldName?: 'supplierOrderItemId' | 'supplierOrderItemIndex';
  onSupplierOrderItemChange?: (value: string | number | null | undefined) => void;
}

const SupplierOrderLinkAllocationFields: React.FC<SupplierOrderLinkAllocationFieldsProps> = ({
  form,
  canEditSupplierOrderFinance,
  supplierOrderItemOptions,
  namePrefix = [],
  itemFieldName = 'supplierOrderItemId',
  onSupplierOrderItemChange,
}) => {
  const { t, i18n } = useTranslation(['orders']);
  const fieldPath = (name: FieldName) => (namePrefix.length > 0 ? [...namePrefix, name] : name);
  const allocateByItem = Form.useWatch(fieldPath('allocateByItem'), form);
  const selectedItemValue = Form.useWatch(fieldPath(itemFieldName), form);
  const allocatedQuantity = Form.useWatch(fieldPath('allocatedQuantity'), form);
  const allocatedUnit = Form.useWatch(fieldPath('allocatedUnit'), form);
  const selectedItem = supplierOrderItemOptions.find(
    (option) => String(option.value) === String(selectedItemValue)
  );
  const targetUnit = selectedItem?.unit || null;
  const selectedItemDimensions = {
    width: selectedItem?.width == null ? null : Number(selectedItem.width),
    length: selectedItem?.length == null ? null : Number(selectedItem.length),
    height: selectedItem?.height == null ? null : Number(selectedItem.height),
    thickness: selectedItem?.thickness == null ? null : Number(selectedItem.thickness),
  };
  const shouldConvert = Boolean(
    selectedItem
    && allocatedQuantity
    && allocatedUnit
    && targetUnit
    && allocatedUnit !== targetUnit
  );
  const conversion = shouldConvert
    ? convertAllocationQuantity({
        quantity: allocatedQuantity,
        sourceUnit: allocatedUnit,
        targetUnit,
        ...selectedItemDimensions,
      })
    : null;
  const locale = i18n?.language?.startsWith('pl') ? 'pl-PL' : 'en-US';
  const formatQuantity = (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 3 });
  const formatDimension = (value: number | string | null | undefined) => (
    Number(value).toLocaleString(locale, { maximumFractionDigits: 2 })
  );
  const formatUnitForConversionError = (unit: string | null | undefined) => (
    unit === 'pcs' && locale === 'pl-PL' ? 'sztuki' : formatUnit(unit, i18n.language)
  );
  const isAreaVolumeConversion = (
    (allocatedUnit === 'm2' && targetUnit === 'm3')
    || (allocatedUnit === 'm3' && targetUnit === 'm2')
  );
  const heightOrThickness = resolveThirdDimensionMm(selectedItemDimensions);
  const dimensionValues = isAreaVolumeConversion
    ? [heightOrThickness]
    : allocatedUnit === 'm3' || targetUnit === 'm3'
      ? [selectedItemDimensions.width, selectedItemDimensions.length, heightOrThickness]
      : [selectedItemDimensions.width, selectedItemDimensions.length];
  const conversionMessage = conversion?.error === 'MISSING_DIMENSIONS'
    ? t('orders:supplierOrders.form.allocationConversionMissingDimensions', {
        sourceUnit: formatUnitForConversionError(allocatedUnit),
        targetUnit: formatUnitForConversionError(targetUnit),
      })
    : conversion?.error === 'UNSUPPORTED_CONVERSION'
      ? t('orders:supplierOrders.form.allocationConversionUnsupported', {
          sourceUnit: formatUnitForConversionError(allocatedUnit),
          targetUnit: formatUnitForConversionError(targetUnit),
        })
      : null;

  return (
    <>
      <Form.Item name={fieldPath('allocateByItem')} valuePropName="checked">
        <Switch
          checkedChildren={t('orders:supplierOrders.form.allocateByItem')}
          unCheckedChildren={t('orders:supplierOrders.form.linkWholeOrder')}
        />
      </Form.Item>
      {allocateByItem && (
        <>
          <Form.Item
            name={fieldPath(itemFieldName)}
            label={t('orders:supplierOrders.form.supplierOrderItem')}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={supplierOrderItemOptions}
              onChange={onSupplierOrderItemChange}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={16}>
              <Form.Item
                name={fieldPath('allocatedQuantity')}
                label={t('orders:supplierOrders.form.allocatedQuantity')}
                rules={[supplierOrderQuantityRule(t)]}
              >
                <InputNumber precision={3} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item
                name={fieldPath('allocatedUnit')}
                label={t('orders:supplierOrders.form.allocatedUnit')}
                rules={[{
                  validator: async (_, value) => {
                    if (allocatedQuantity && !value) {
                      throw new Error(t('orders:supplierOrders.form.allocationUnitRequired'));
                    }
                    if (conversionMessage) {
                      throw new Error(conversionMessage);
                    }
                  },
                }]}
              >
                <Select
                  options={ALLOCATION_UNITS.map((unit) => ({
                    value: unit,
                    label: formatUnit(unit, i18n.language),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          {conversion?.value !== null && conversion?.value !== undefined && !conversion.error && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t(isAreaVolumeConversion
                ? 'orders:supplierOrders.form.allocationConversionThicknessHint'
                : 'orders:supplierOrders.form.allocationConversionHint', {
                sourceQuantity: formatQuantity(Number(allocatedQuantity)),
                sourceUnit: formatUnit(allocatedUnit, i18n.language),
                targetQuantity: formatQuantity(conversion.value),
                targetUnit: formatUnit(targetUnit, i18n.language),
                dimensions: dimensionValues.map(formatDimension).join(' × '),
                thickness: formatDimension(heightOrThickness),
              })}
            />
          )}
          {conversionMessage && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={conversionMessage}
            />
          )}
          <SupplierOrderManualCostOverride
            visible={canEditSupplierOrderFinance}
            name={fieldPath('allocatedCostPLN')}
            addonAfter="PLN"
          />
        </>
      )}
    </>
  );
};

export default SupplierOrderLinkAllocationFields;
